"use client";

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { KaitMark, KaitWordmark } from "./Brand";

// Боковой рельс — навигация рабочего места.
//
// Свёрнут по умолчанию до иконок, раскрывается по наведению и показывает
// названия с подсказками. Раскрытие ПЕРЕКРЫВАЕТ контент, а не раздвигает его:
// иначе таблица под курсором дёргалась бы на каждом заходе мышью в левый край,
// а на плотной сетке смен это ломало бы попадание по клетке.
//
// Тёмный в обеих темах: рельс — якорь экрана, а не поверхность контента.

export type RailItem = {
  key: string;
  title: string;
  hint: string;
  icon: keyof typeof ICONS;
};

const ICONS = {
  dashboard: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
  // Сеть ресторанов (17.09): три узла и связи.
  network: "M12 3a3 3 0 100 6 3 3 0 000-6zM5 15a3 3 0 100 6 3 3 0 000-6zm14 0a3 3 0 100 6 3 3 0 000-6zM12 9v2M7.5 15l3.5-4m2 0l3.5 4",
  schedule: "M3 6a2 2 0 012-2h14a2 2 0 012 2v13a2 2 0 01-2 2H5a2 2 0 01-2-2zM16 2v4M8 2v4M3 10h18",
  employees: "M9 11a4 4 0 100-8 4 4 0 000 8zM2 21v-1a5 5 0 015-5h4a5 5 0 015 5v1M17 11a3 3 0 100-6",
  tasks: "M9 11l2.5 2.5L20 5M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11",
  tests: "M12 14a6 6 0 100-12 6 6 0 000 12zM8.2 13.6L7 22l5-3 5 3-1.2-8.4",
  // Планшет с галочками: чек-листы смены (13.08). Отличается от `tasks` тем,
  // что галочек несколько — это набор пунктов, а не одно поручение.
  checklists:
    "M9 2h6a1 1 0 011 1v1a1 1 0 01-1 1H9a1 1 0 01-1-1V3a1 1 0 011-1zM8 4H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2M8.5 11l1.5 1.5L13 9.5M8.5 16.5l1.5 1.5L13 15",
  menu: "M4 19.5A2.5 2.5 0 016.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z",
  inventory:
    "M21 8v8a2 2 0 01-1 1.73l-7 4a2 2 0 01-2 0l-7-4A2 2 0 013 16V8a2 2 0 011-1.73l7-4a2 2 0 012 0l7 4A2 2 0 0121 8zM3.3 7L12 12l8.7-5M12 22V12",
  reports: "M21 12V7H5a2 2 0 110-4h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4z",
  // Четырёхлучевая искра — СИММЕТРИЧНО вокруг центра 12,12 (правка 23.09:
  // прежняя занимала по высоте 3–17,5 и сидела в кружке выше центра).
  insights: "M12 4.5L13.9 10.1L19.5 12L13.9 13.9L12 19.5L10.1 13.9L4.5 12L10.1 10.1Z",
  analytics: "M3 3v18h18M8 16v-5M13 16V8M18 16v-8",
  // Облачко реплики: чат команды (30.10).
  chat: "M21 11.5a8.5 8.5 0 01-12.4 7.6L3 21l1.9-5.6A8.5 8.5 0 1121 11.5z",
  // Облако реплики с сердцем: отзывы гостей (волна 4.1, 02.09).
  // Облако уменьшено и поднято (правка 23.09): вместе с хвостиком занимало по
  // высоте 4–23 и сидело в кружке ниже центра. Теперь 3–20, сердце — в
  // середине облака.
  reviews: "M21 10a7 7 0 01-7 7H8l-5 3V10a7 7 0 017-7h4a7 7 0 017 7zM12 12.5l-2.4-2.3a1.4 1.4 0 012-2l.4.4.4-.4a1.4 1.4 0 012 2z",
  // Калькулятор: корпус, окошко и две строки клавиш. Раздел «Расчёты» —
  // прикидка блюда без заведения карты (заказ Марата 08.08).
  calc: "M6 2h12a2 2 0 012 2v16a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2zM8 6h8v3H8zM8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01",
  settings:
    "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z",
  // ── Разделы консоли оператора (19.09) ──
  // Рельс переехал из рабочего места в консоль, и ей нужны свои значки:
  // журнал действий, ключи администраторов и сломавшееся у клиентов.
  journal: "M5 4a2 2 0 012-2h9l4 4v14a2 2 0 01-2 2H7a2 2 0 01-2-2zM15 2v5h5M9 12h7M9 16h5",
  keys: "M14.5 10.5a4 4 0 10-4.6 3.95L8 16.35l1.5 1.5-1.2 1.2 1.6 1.6 2.2-2.2v-4a4 4 0 002.4-3.95zM16 7.5h.01",
  alert: "M12 3.5 21 19H3zM12 10v4M12 16.5h.01",
} as const;

/* Движение подбирается ПО ЗНАЧКУ, а не одно на всех (правка Марата 06.08
   вечером — «на шестерёнке нет прокрутки»): шестерёнка обязана крутиться, где
   бы ни стояла, звезда «Нового» — подмигивать поворотом. Остальные просто
   подрастают: в рельсе движение самое тихое из всех, мышь заходит сюда каждый
   раз, когда идёт к краю экрана. */
const ICON_MOTION: Partial<Record<keyof typeof ICONS, string>> = {
  settings: "group-hover/item:rotate-90",
  insights: "group-hover/item:rotate-[18deg] group-hover/item:scale-125",
  // Движение ПО СМЫСЛУ значка и с заметной амплитудой (правка Марата 07.08:
  // «шестерёнку заметно, остальные еле-еле»). Календарь приоткрывается, лист
  // задач подписывают наклоном, коробка склада качается, деньги подскакивают.
  schedule: "group-hover/item:-rotate-6 group-hover/item:scale-110",
  tasks: "group-hover/item:rotate-[-8deg] group-hover/item:scale-125",
  employees: "group-hover/item:scale-125",
  inventory: "group-hover/item:rotate-[10deg] group-hover/item:scale-110",
  reports: "group-hover/item:-translate-y-0.5 group-hover/item:scale-125",
  analytics: "group-hover/item:scale-125",
  dashboard: "group-hover/item:rotate-45 group-hover/item:scale-110",
  // Калькулятор чуть приподнимается — как будто его берут в руку.
  calc: "group-hover/item:-translate-y-0.5 group-hover/item:scale-110",
  // Консоль: журнал листают, ключ поворачивают, треугольник тревоги дрожит.
  journal: "group-hover/item:-rotate-6 group-hover/item:scale-110",
  keys: "group-hover/item:rotate-[20deg] group-hover/item:scale-110",
  alert: "group-hover/item:scale-125",
};

function Icon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={[
        "h-5 w-5 shrink-0 transition-transform duration-300 ease-out",
        ICON_MOTION[name] ?? "group-hover/item:scale-125",
      ].join(" ")}
      aria-hidden
    >
      <path d={ICONS[name]} />
    </svg>
  );
}

export default function Rail({
  items,
  current,
  onSelect,
  footer,
}: {
  items: RailItem[];
  current: string;
  onSelect: (key: string) => void;
  footer?: ReactNode;
}) {
  /* ─── Кружок активного раздела ПЕРЕЕЗЖАЕТ (заказ Марата 08.08) ───

     Раньше он просто загорался на новом месте и гас на старом. По правилу
     Марата 07.08 любой выбор из нескольких — это shared element: подсветка
     должна ехать, потому что выбор один и он сдвинулся.

     Меряем реальную позицию активной кнопки, а не считаем по формуле
     «индекс × высота». Формула сломалась бы на первом же скрытом разделе: у
     менеджера зоны часть пунктов не показывается (права + модули), и номер
     пункта перестал бы совпадать с его местом. */
  const navRef = useRef<HTMLElement>(null);
  const [y, setY] = useState<number | null>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      // Меряем САМ КРУЖОК значка, а не кнопку, в которой он лежит.
      //
      // Кнопка выше кружка на свои вертикальные поля (`py-1`), и подложка,
      // поставленная по её верху, оказывалась на 4 пикселя выше значка —
      // активный значок сидел не по центру круга. Магическую поправку «+4»
      // ставить нельзя: поменяется padding, и всё разъедется снова.
      const dot = nav.querySelector<HTMLElement>('[data-active="true"] [data-rail-dot]');
      if (!dot) return;
      const navRect = nav.getBoundingClientRect();
      const dotRect = dot.getBoundingClientRect();
      setY(dotRect.top - navRect.top + nav.scrollTop);
    };
    measure();
    // Рельс раскрывается по наведению и может прокручиваться — позиция кнопки
    // от этого меняется, и кружок обязан переехать вместе с ней.
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [current, items]);

  return (
    // Внешний элемент держит в потоке ровно 72px — ширину свёрнутого рельса.
    // Раскрытая панель внутри выходит за эти границы поверх контента.
    <aside className="group/rail relative w-[72px] shrink-0">
      <div
        className="fixed left-0 top-0 z-40 flex h-dvh w-[72px] flex-col overflow-hidden bg-[var(--op-rail)] transition-[width] duration-[240ms] group-hover/rail:w-[264px] group-hover/rail:shadow-[0_0_60px_-12px_rgba(0,0,0,0.45)]"
        style={{ transitionTimingFunction: "var(--op-ease-out)" }}
      >
        {/* Знак и логотип лежат друг на друге и меняются прозрачностью: в
            свёрнутом рельсе виден знак, в раскрытом — знак с начертанием.

            Знак стоит В ТОЙ ЖЕ 40-пиксельной колонке, что и кружки навигации
            (px-4 + w-10), и центрируется в ней — раньше он был прижат к 26px и
            в свёрнутом рельсе стоял не по оси кружков (правка Марата 05.08).
            Wordmark начинается от того же левого края, поэтому при раскрытии
            знак не прыгает. */}
        <div className="relative flex h-[72px] shrink-0 items-center px-4">
          {/* Знак ОСТАЁТСЯ при раскрытии, надпись KLAIT выезжает рядом
              (правка Марата 21.09, [273]: «при открытии рельсы логотип
              меняется на надпись — сделай так, чтобы открывалась надпись
              KLAIT и значок так же оставался»). Раньше знак гас, а на его
              месте проявлялось слово — это читалось как подмена логотипа. */}
          <span className="grid w-10 shrink-0 place-items-center">
            <KaitMark className="h-[17px] w-auto max-w-none text-[var(--op-rail-fg-strong)]" />
          </span>
          <KaitWordmark
            aria-hidden
            className="ml-2.5 -translate-x-1 whitespace-nowrap text-[17px] text-[var(--op-rail-fg-strong)] opacity-0 transition-[opacity,transform] duration-200 group-hover/rail:translate-x-0 group-hover/rail:opacity-100"
          />
        </div>

        <nav ref={navRef} className="relative flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-4 py-2">
          {/* Едет на transform: он не трогает раскладку и идёт по GPU. До
              первого замера кружка нет вовсе — иначе он проехал бы через весь
              рельс на первом кадре. */}
          {y !== null && (
            <span
              aria-hidden
              className="op-glow-accent pointer-events-none absolute left-4 top-0 h-10 w-10 rounded-full bg-[var(--op-lime)] transition-transform duration-300"
              style={{
                transform: `translateY(${y}px)`,
                transitionTimingFunction: "var(--op-ease-out)",
              }}
            />
          )}
          {items.map((it) => {
            const active = it.key === current;
            return (
              <button
                key={it.key}
                onClick={() => onSelect(it.key)}
                title={it.title}
                aria-current={active ? "page" : undefined}
                data-active={active}
                // Обучение при первом входе (C5-А): цель тура — `rail-<key>`,
                // те же ключи, что в SECTIONS в _desk/page.tsx.
                data-tour={`rail-${it.key}`}
                className="group/item relative z-10 flex shrink-0 items-center gap-3 rounded-full py-1 pr-3 text-left"
              >
                {/* Активный раздел отмечает КРУЖОК, а не подложка строки. В
                    свёрнутом виде видны только кружки — значит именно они и
                    обязаны нести состояние, иначе на 72 пикселях непонятно, где
                    ты находишься.

                    Ховер — у КАЖДОГО кружка свой (правка Марата 05.08: раньше
                    подсвечивался весь рельс разом, и отклика под курсором не
                    было): тёплая заливка + оранжевый кант + белая иконка. */}
                <span
                  data-rail-dot
                  className={[
                    "grid h-10 w-10 shrink-0 place-items-center rounded-full transition-[background-color,color,box-shadow] duration-150",
                    // ⚠️ У активного СВОЕЙ заливки нет: её несёт переезжающий
                    // кружок под ним. Оставь фон здесь — и на время переезда
                    // оранжевых кружков было бы два.
                    active
                      ? "text-white"
                      : "text-[var(--op-rail-fg)] ring-1 ring-inset ring-[var(--op-rail-line)] group-hover/item:bg-[var(--op-rail-hover)] group-hover/item:text-[var(--op-rail-fg-strong)] group-hover/item:ring-[var(--op-lime)]",
                  ].join(" ")}
                >
                  <Icon name={it.icon} />
                </span>
                {/* Ширина подписи ФИКСИРОВАННАЯ, а не `flex-1` (ревью анимаций
                    06.08). Растягиваясь вместе с рельсом, блок пересчитывал
                    обрезку на каждом кадре: названия разделов ужимались и
                    разжимались всю дорогу раскрытия. Теперь текст свёрстан
                    сразу на полную ширину, а рельс его просто открывает. */}
                <span
                  className={[
                    "w-[184px] shrink-0 opacity-0 transition-opacity duration-150 group-hover/rail:opacity-100",
                    active
                      ? "text-[var(--op-rail-fg-strong)]"
                      : "text-[var(--op-rail-fg)] group-hover/item:text-[var(--op-rail-fg-strong)]",
                  ].join(" ")}
                >
                  <span className="block truncate whitespace-nowrap text-[13px] font-semibold leading-tight">
                    {it.title}
                  </span>
                  <span className="block truncate whitespace-nowrap text-[11px] leading-tight opacity-70">
                    {it.hint}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        {footer && (
          <div className="shrink-0 border-t border-[var(--op-rail-line)] px-4 py-3">{footer}</div>
        )}
      </div>
    </aside>
  );
}
